import { getUrlData } from './project-cards';
import { FilterCategories, FilterCategory } from "../typings/backend-models";

interface CreateFilter {
    mainElement: HTMLElement;
    initialActiveCategoriesIds?: number[];
    onSubmit: (categoriesIds: number[]) => void;
    filterCategories: FilterCategories;
    abortText: string;
    submitText: string;
    filterChooseText: string;
    filterTitle: string;
    isAsync: Boolean;
    language: string;
}

const updateActiveFilterButtonText = () => {
    const activeButton = document.querySelector('.filter-active-button');
    const selectedFiltersContainer = document.querySelector('.selectedFilters');
    const iconSvg = activeButton?.querySelector('svg')?.outerHTML || '';
    const filterCount = selectedFiltersContainer?.children.length || 0;

    if (activeButton) {
        if (filterCount > 0) {
            const badge = `<span style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                background-color: #4D42ca;
                color: white;
                border-radius: 50%;
                width: 24px;
                height: 24px;
                font-size: 0.875rem;
                font-weight: bold;
                line-height: 1;
                padding-top: 1px;
            ">${filterCount}</span>`;

            activeButton.innerHTML = `${iconSvg} Filter ${badge}`;
        } else {
            activeButton.innerHTML = `${iconSvg} Filter`;
        }
    }
};

const toggleSelectedFiltersLabel = () => {
    const container = document.querySelector('.selected-filters-container') as HTMLElement;
    const wrapper = document.querySelector('.selected-filters-wrapper') as HTMLElement;
    const selectedFiltersContainer = document.querySelector('.selectedFilters');
    const hasFilters = selectedFiltersContainer?.children.length && selectedFiltersContainer.children.length > 0;

    if (wrapper && container) {
        wrapper.style.display = hasFilters ? 'block' : 'none';
        container.style.display = hasFilters ? 'block' : 'none';
    }
    updateActiveFilterButtonText();
};

const filterSideNav = () => {
    const filterComponentEl = document.querySelector('.filter-component');
    if (!filterComponentEl) return;     
    const isExpanded = (element: Element): Boolean => {
        return element.classList.contains('expanded');
    };

    const sideNavElement = filterComponentEl.querySelector('.filter-side-nav');
    const activeButton = filterComponentEl.querySelector('.filter-active-button');
    const overlay = document.querySelector('.filter-overlay');

    if (!sideNavElement || !activeButton || !overlay) return;

    const filterDefaultText = 'Filter';
    const { closeText } = (activeButton as HTMLElement).dataset;

    activeButton.addEventListener('click', function () {
        const isNowOpen = !isExpanded(sideNavElement);
        sideNavElement.classList.toggle('expanded', isNowOpen);
        activeButton.classList.toggle('filter-active', isNowOpen);
        activeButton.classList.toggle('button--primary__active', isNowOpen);
        overlay.classList.toggle('visible', isNowOpen);
    });
}

const createFilter = (props: CreateFilter): void => {
    const { mainElement, onSubmit, submitText, abortText, filterChooseText, filterCategories, filterTitle, isAsync, language } = props;

    let activeCategoryIds: number[] = [];
    let committedCategoryIds: number[] = []; // Tracks actually submitted filters

    // Helper function to get all child category IDs one level down
    const getChildCategoryIds = (category: FilterCategory): number[] => {
        const ids: number[] = [];
        if (category.categories) {
            category.categories.forEach((subcategory) => {
                ids.push(Number(subcategory.id));
            });
        }
        return ids;
    };

    const createMainCategory = (category: FilterCategory) => {
        const wrapper = document.createElement('div');
        wrapper.classList.add('filter-item', 'filter-item__main');

        const labelEl = document.createElement('h4');
        labelEl.classList.add('filter-item__label');
        labelEl.textContent = category.name;

        wrapper.append(labelEl);

        const panel = document.createElement('ul');
        panel.setAttribute('id', "parentname-" + category.name);
        panel.classList.add('filter-item__panel', 'filter-item__panel--first-level');
        panel.style.maxHeight = '1600px';

        wrapper.append(panel);

        if (category.categories) {
            category.categories.forEach((subcategory) => {
                const isSingleItem = subcategory.categories === null;
                const isSubcategory = !isSingleItem;
                const isAllWithin = subcategory.isAllWithin;

                if (isSingleItem || isAllWithin) {
                    const subItem = createSingleItem(subcategory);
                    panel.append(subItem);
                } else if (isSubcategory) {
                    const subItems = createSubcategory(subcategory);
                    panel.append(subItems);
                }
            });
        }

        return wrapper;
    };

    const createSingleItem = (category: FilterCategory) => {
        const catIdNum = Number(category.id);

        const item = document.createElement('li');
        item.classList.add('filter-item', 'filter-item__selectable', 'subcategory_container');
        item.setAttribute('data-id', category.id.toString());

        const itemContentEl = document.createElement('label');
        itemContentEl.textContent = category.name;
        itemContentEl.setAttribute('for', 'cat' + category.id);
        itemContentEl.classList.add('subcategory_select')

        const checkboxEl = document.createElement('input');
        checkboxEl.setAttribute('type', 'checkbox');
        checkboxEl.classList.add('filter-item__checkbox');
        checkboxEl.setAttribute('value', category.name);
        checkboxEl.setAttribute('name', category.name);
        checkboxEl.setAttribute('id', 'cat' + category.id);

        item.append(checkboxEl);
        item.append(itemContentEl);

        // Stop propagation on label clicks as well
        itemContentEl.addEventListener('click', function (e) {
            e.stopPropagation();
        });

        checkboxEl.addEventListener('click', function (e) {
            e.stopPropagation();
            const isChecked = checkboxEl.checked;
            if (isChecked) {
                addFilter(catIdNum, category.name, true);
            } else {
                removeFilter(catIdNum, true);
            }
            updateParentCheckboxState(item);
        });

        return item;
    };

    const createSubcategory = (category: FilterCategory) => {
        const { name, categories, id } = category;
        const isMainCategory = category.isTopCategoryInFilter;

        const wrapper = isMainCategory ? document.createElement('div') : document.createElement('li');
        wrapper.classList.add('filter-item');

        if (!isMainCategory) {
            const item = document.createElement('div');
            item.classList.add('filter-item', 'filter-item__selectable', "firstlevel-border");
            item.setAttribute('data-id', id.toString());

            const itemContentEl = document.createElement('label');
            itemContentEl.textContent = name;
            itemContentEl.setAttribute('for', 'cat' + id);
            itemContentEl.classList.add('filter-item__label');

            const checkboxEl = document.createElement('input');
            checkboxEl.setAttribute('type', 'checkbox');
            checkboxEl.classList.add('filter-item__checkbox');
            checkboxEl.setAttribute('value', name);
            checkboxEl.setAttribute('name', name);
            checkboxEl.setAttribute('id', 'cat' + id);

            item.append(checkboxEl);
            item.append(itemContentEl);

            const toggleBtn = document.createElement('button');
            toggleBtn.type = 'button';
            toggleBtn.classList.add('filter-toggle-arrow');
            item.append(toggleBtn);

            // Stop propagation on label clicks
            itemContentEl.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            toggleBtn.addEventListener('click', (e) => {
                const panel = item.nextElementSibling as HTMLElement;
                const isOpen = panel.classList.contains('expanded');
                panel.classList.toggle('expanded', !isOpen);
                panel.style.maxHeight = isOpen ? '0' : panel.scrollHeight + 'px';
                toggleBtn.classList.toggle('open', !isOpen);
                e.stopPropagation();
            });

            checkboxEl.addEventListener('click', function (e) {
                e.stopPropagation();
                
                const isChecked = checkboxEl.checked;
                const childIds = getChildCategoryIds(category);
                if (isChecked) {
                    childIds.forEach((childId) => {
                        const childItem = document.querySelector(`.filter-item__selectable[data-id="${childId}"]`);
                        if (childItem) {
                            const childCheckbox = childItem.querySelector('.filter-item__checkbox') as HTMLInputElement;
                            if (!childCheckbox.checked) {
                                const childName = childCheckbox.getAttribute('name') || '';
                                addFilter(childId, childName, true);
                            }
                        }
                    });
                } else {
                    childIds.forEach((childId) => {
                        const childItem = document.querySelector(`.filter-item__selectable[data-id="${childId}"]`);
                        if (childItem) {
                            const childCheckbox = childItem.querySelector('.filter-item__checkbox') as HTMLInputElement;
                            if (childCheckbox.checked) {
                                removeFilter(childId, true);
                            }
                        }
                    });
                }
            });

            wrapper.append(item);
        }

        const panel = document.createElement('ul');
        panel.setAttribute('id', "parentname-" + name);
        panel.classList.add('filter-item__panel', 'filter-item__panel--first-level');
        panel.style.maxHeight = '0';
        panel.classList.remove('expanded');

        wrapper.append(panel);

        if (categories) {
            categories.forEach((subcategory) => {
                const isSingleItem = subcategory.categories === null;
                const isSubcategory = !isSingleItem;
                const isAllWithin = subcategory.isAllWithin;

                if (isSingleItem || isAllWithin) {
                    const subItem = createSingleItem(subcategory);
                    panel.append(subItem);
                } else if (isSubcategory) {
                    const subItems = createSubcategory(subcategory);
                    panel.append(subItems);
                }
            });
        }

        return wrapper;
    };

    const updateParentCheckboxState = (item: Element) => {
        const parentPanel = item.closest('.filter-item__panel');
        if (parentPanel) {
            const parentWrapper = parentPanel.closest('.filter-item');
            if (parentWrapper) {
                const parentCheckbox = parentWrapper.querySelector('.filter-item__checkbox') as HTMLInputElement;
                // Only update if parentCheckbox exists and is NOT inside the panel
                // This prevents updating sibling checkboxes in single-level filter lists
                if (parentCheckbox && !parentPanel.contains(parentCheckbox)) {
                    const childCheckboxes = parentPanel.querySelectorAll('.filter-item__checkbox');
                    const allChildrenChecked = Array.from(childCheckboxes).every(cb => (cb as HTMLInputElement).checked);
                    parentCheckbox.checked = allChildrenChecked;
                }
            }
        }
    };

    const removeFilter = (id: number, skipSubmit: boolean = false) => {
        const filterItem = document.querySelector(`.filter-item__selectable[data-id="${id.toString()}"]`);
        if (filterItem) {
            const filterCheckbox = filterItem.querySelector('.filter-item__checkbox') as HTMLInputElement;
            filterCheckbox.checked = false;
            activeCategoryIds = activeCategoryIds.filter((num) => num !== id);
            filterItem.classList.remove('active');
            if (!skipSubmit) {
                removeFilterListItem(id);
            }
            updateParentCheckboxState(filterItem);
            if (!skipSubmit) {
                onSubmit(activeCategoryIds);
            }
        }
    };

    const addFilter = (id: number, name: string, skipSubmit: boolean = false) => {
        const filterItem = document.querySelector(`.filter-item__selectable[data-id="${id.toString()}"]`);
        if (filterItem) {
            const filterCheckbox = filterItem.querySelector('.filter-item__checkbox') as HTMLInputElement;
            filterCheckbox.checked = true;
            filterItem.classList.add('active');
            if (!activeCategoryIds.includes(id)) {
                activeCategoryIds.push(id);
            }
            if (!skipSubmit) {
                createFilterListItem(id, name);
            }
            updateParentCheckboxState(filterItem);
            if (!skipSubmit) {
                onSubmit(activeCategoryIds);
            }
        }
    };

    const removeAllFilters = () => {
        const idsToRemove = [...activeCategoryIds];
        idsToRemove.forEach((catId) => {
            removeFilter(catId, false);
        });
        activeCategoryIds = [];
        updateActiveFilterButtonText();
    };

    const resetCategories = (isAsync: Boolean, idNameMap: Map<number, string>) => {
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;
        searchParams.delete('catids');
        searchParams.delete('page');
        searchParams.delete('q'); // Remove search term
        url.search = searchParams.toString();

        // Clear search input field
        const searchInput = document.querySelector('input.input-cards-filter') as HTMLInputElement;
        if (searchInput) {
            searchInput.value = '';
        }

        if (isAsync) {
            removeAllFilters();
            history.pushState({}, '', url.toString());
            // Refresh the selected filters display
            renderSelectedFilters(idNameMap);
            // Force page reload to ensure all filters are properly cleared
            window.location.href = url.toString();
        } else {
            window.open(url.href, '_self');
        }
    };

    const createFilterListItem = (id: number, name: string) => { 
        const selectedFiltersContainer = document.querySelector('.selectedFilters');
        if (!selectedFiltersContainer) {
            console.error('selectedFilters container not found');
            return;
        }

        // Prevent duplicates by checking if filter already exists
        if (selectedFiltersContainer.querySelector(`button[data-id="${id}"]`)) {
            return;
        }

        const filterEl = document.createElement('button');
        filterEl.setAttribute('type', 'button');
        filterEl.classList.add('filter-selected-button');
        filterEl.setAttribute('data-id', id.toString());
        filterEl.innerHTML = `
            ${name}
            <span class="filter-selected-icon"></span>
        `;

        filterEl.addEventListener('click', function () {
            removeFilter(id);
        });
        selectedFiltersContainer.appendChild(filterEl);
        toggleSelectedFiltersLabel();
    };

    const removeFilterListItem = (id: number) => {
        const selectedFiltersContainer = document.querySelector('.selectedFilters');
        const filterItem = selectedFiltersContainer?.querySelector(`button[data-id="${id}"]`);
        if (filterItem) {
            filterItem.remove();
        }
        toggleSelectedFiltersLabel();
    };

    const createFiltersList = (categories: FilterCategory[], submitText: string, abortText: string, filterChooseText: string, filterTitle: string) => {
        const filterWrapperEl = mainElement;
 
        const filters: Element[] = [];

        const filterListEl = document.createElement('div');
        filterListEl.classList.add('filter-list');
        
        const buttonsWrapperEl = document.createElement('div');
        buttonsWrapperEl.classList.add('filter-buttons');

        const submitButton = document.createElement('button');
        submitButton.classList.add('filter-button', 'button', 'startPageButton', 'buttonPrimary', 'filter-button--submit', 'button--negative');
        submitButton.textContent = submitText;

        submitButton.addEventListener('click', () => {
            // Save committed filters
            committedCategoryIds = [...activeCategoryIds];
            
            // Render all selected filters
            renderSelectedFilters(idNameMap);
            
            onSubmit(activeCategoryIds);

            const filterComponentEl = document.querySelector('.filter-component');
            const sideNavElement = filterComponentEl?.querySelector('.filter-side-nav');
            const activeButton = filterComponentEl?.querySelector('.filter-active-button');
            const overlay = document.querySelector('.filter-overlay');

            if (sideNavElement && activeButton) {
                sideNavElement.classList.remove('expanded');
                activeButton.classList.remove('button--primary__active');
            }

            if (overlay) {
                overlay.classList.remove('visible');
            }
        });

        const abortButton = document.createElement('button');
        abortButton.classList.add('filter-button', 'button', 'abort-button', 'button--outline-negative', 'filter-button--abort');
        abortButton.textContent = abortText;
        abortButton.addEventListener('click', () => {
            resetCategories(isAsync, idNameMap);

            const filterComponentEl = document.querySelector('.filter-component');
            const sideNavElement = filterComponentEl?.querySelector('.filter-side-nav');
            const activeButton = filterComponentEl?.querySelector('.filter-active-button');
            const overlay = document.querySelector('.filter-overlay');

            if (sideNavElement && activeButton) {
                sideNavElement.classList.remove('expanded');
                activeButton.classList.remove('button--primary__active');
            }

            if (overlay) {
                overlay.classList.remove('visible');
            }
        });

        buttonsWrapperEl.append(abortButton, submitButton);

        categories.forEach((category) => {
            const firstItemEl = createMainCategory(category);
            filterListEl.append(firstItemEl);
            filters.push(filterListEl);
        });

        filters.forEach((filter) => {
            filterWrapperEl.prepend(filter);
        });
        
        filterWrapperEl.append(buttonsWrapperEl);

        // Create header container for title and close button
        const filterHeaderEl = document.createElement('div');
        filterHeaderEl.classList.add('filter-header');

        const filterTitleEl = document.createElement('h2');
        filterTitleEl.textContent = filterTitle;
        filterTitleEl.classList.add("filter-title");
        
        // Move mobile close button into header
        const sideNavElement = filterWrapperEl.closest('.filter-side-nav');
        const mobileButton = sideNavElement?.querySelector('.filter-side-nav__button-mobile');
        
        filterHeaderEl.append(filterTitleEl);
        if (mobileButton) {
            filterHeaderEl.append(mobileButton);
        }
        
        filterWrapperEl.prepend(filterHeaderEl);

        const removeAllFilterButton = document.querySelector('.removeAllFilter');
        if (removeAllFilterButton) {
            removeAllFilterButton.addEventListener('click', () => {
                resetCategories(isAsync, idNameMap);

                const filterComponentEl = document.querySelector('.filter-component');
                const sideNavElement = filterComponentEl?.querySelector('.filter-side-nav');
                const activeButton = filterComponentEl?.querySelector('.filter-active-button');
                const overlay = document.querySelector('.filter-overlay');

                if (sideNavElement && activeButton) {
                    sideNavElement.classList.remove('expanded');
                    activeButton.classList.remove('button--primary__active');
                }

                if (overlay) {
                    overlay.classList.remove('visible');
                }
            });
        }
    };

    const idNameMap = new Map<number, string>();
    flattenCategories(filterCategories.categories, idNameMap);

    // Initialize activeCategoryIds from URL catIds
    const { catIds } = getUrlData();
    activeCategoryIds = [...catIds];
    committedCategoryIds = [...catIds]; // Initialize committed filters from URL

    createFiltersList(filterCategories.categories, submitText, abortText, filterChooseText, filterTitle);

    // Initialize checkboxes from URL catIds AFTER the DOM elements are created
    catIds.forEach(id => {
        const filterItem = document.querySelector(`.filter-item__selectable[data-id="${id}"]`);
        if (filterItem) {
            const checkbox = filterItem.querySelector('.filter-item__checkbox') as HTMLInputElement;
            checkbox.checked = true;
            filterItem.classList.add('active');
            updateParentCheckboxState(filterItem);
        }
    });

    // Render selected filters from URL
    renderSelectedFilters(idNameMap);

    // Setup mobile close button
    const filterComponentEl = document.querySelector('.filter-component');
    const sideNavElement = filterComponentEl?.querySelector('.filter-side-nav');
    const mobileButton = sideNavElement?.querySelector('.filter-side-nav__button-mobile');
    const activeButton = filterComponentEl?.querySelector('.filter-active-button');
    const overlay = document.querySelector('.filter-overlay');

    if (mobileButton && sideNavElement && activeButton && overlay) {
        mobileButton.addEventListener('click', function () {
            // Reset to committed filters
            activeCategoryIds = [...committedCategoryIds];
            
            // Update checkboxes to match committed state
            document.querySelectorAll('.filter-item__selectable').forEach((item) => {
                const id = Number(item.getAttribute('data-id'));
                const checkbox = item.querySelector('.filter-item__checkbox') as HTMLInputElement;
                if (checkbox) {
                    checkbox.checked = committedCategoryIds.includes(id);
                    if (checkbox.checked) {
                        item.classList.add('active');
                    } else {
                        item.classList.remove('active');
                    }
                }
            });
            
            sideNavElement.classList.remove('expanded');
            updateActiveFilterButtonText();
            activeButton.classList.remove('button--primary__active');
            overlay.classList.remove('visible');
        });
    }

    const searchWrapper = document.querySelector('.input-search-wrapper');
    if (searchWrapper) {
        const searchInput = searchWrapper.querySelector('input.input-cards-filter') as HTMLInputElement;
        const searchIcon = searchWrapper.querySelector('.search-icon');
        
        // Set initial search input value from URL
        const { searchTerm } = getCurrentFilterState();
        if (searchInput && searchTerm) {
            searchInput.value = searchTerm;
        }
        
        const setSearchTermInUrl = (keyword: string) => {
            const url = new URL(window.location.href);
            if (keyword) {
                url.searchParams.set('q', keyword);
            } else {
                url.searchParams.delete('q');
            }
            history.pushState({}, '', url.toString());
            // Refresh the selected filters display to show/hide search term
            renderSelectedFilters(idNameMap);
        };
        searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const keyword = searchInput.value.trim();
                setSearchTermInUrl(keyword);
            }
        });
        if (searchIcon) {
            searchIcon.addEventListener('click', () => {
                const keyword = searchInput.value.trim();
                setSearchTermInUrl(keyword);
            });
        }
    }
};

function flattenCategories(categories: FilterCategory[], map: Map<number, string>) {
    categories.forEach(cat => {
        map.set(cat.id, cat.name);
        if (cat.categories && cat.categories.length > 0) {
            flattenCategories(cat.categories, map);
        }
    });
}

function getCurrentFilterState() {
    const { catIds, searchTerm } = getUrlData();
    return { catIds, searchTerm };
}

function getSearchTermLabel(): string {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('sv')) return 'Sökord';
    if (lang.startsWith('en')) return 'Keyword';
    return 'Søkeord';
}

function setSelectedFilterContent(filterEl: HTMLElement, text: string) {
    // textContent so URL/category values are never parsed as HTML
    filterEl.textContent = `${text} `;
    const icon = document.createElement('span');
    icon.classList.add('filter-selected-icon');
    filterEl.appendChild(icon);
}

function renderSelectedFilters(idNameMap: Map<number, string>) {
    const selectedFiltersContainer = document.querySelector('.selectedFilters');
    if (!selectedFiltersContainer) return;
    selectedFiltersContainer.innerHTML = '';
    const { catIds, searchTerm } = getCurrentFilterState();
    catIds.forEach(id => {
        const name = idNameMap.get(id) || `Kategori ${id}`;
        const filterEl = document.createElement('button');
        filterEl.setAttribute('type', 'button');
        filterEl.classList.add('filter-selected-button');
        filterEl.setAttribute('data-id', id.toString());
        setSelectedFilterContent(filterEl, name);
        filterEl.addEventListener('click', function () {
            const { catIds } = getCurrentFilterState();
            const newCatIds = catIds.filter(cid => cid !== id);
            updateCategories(newCatIds, true);
        });
        selectedFiltersContainer.appendChild(filterEl);
    });
    if (searchTerm) {
        const filterEl = document.createElement('button');
        filterEl.setAttribute('type', 'button');
        filterEl.classList.add('filter-selected-button');
        filterEl.setAttribute('data-type', 'search');
        setSelectedFilterContent(filterEl, `${getSearchTermLabel()}: ${searchTerm}`);
        filterEl.addEventListener('click', function () {
            const url = new URL(window.location.href);
            url.searchParams.delete('q');
            url.searchParams.delete('page');
            
            // Clear search input field
            const searchInput = document.querySelector('input.input-cards-filter') as HTMLInputElement;
            if (searchInput) {
                searchInput.value = '';
            }
            
            // Update URL and reload page to refresh results
            window.location.href = url.toString();
        });
        selectedFiltersContainer.appendChild(filterEl);
    }
    toggleSelectedFiltersLabel();
}

const getCategories = async (url: string) => {
    const res = await fetch(url).then((response) => response.json());
    return res;
};

const setupOverlayClickClose = () => {
    const overlay = document.querySelector('.filter-overlay');
    const sideNavElement = document.querySelector('.filter-side-nav');
    const activeButton = document.querySelector('.filter-active-button');

    if (!overlay || !sideNavElement || !activeButton) return;

    // Prevent clicks inside sidenav from closing the filter
    sideNavElement.addEventListener('click', (e) => {
        e.stopPropagation();
    });

    overlay.addEventListener('click', () => {
        overlay.classList.remove('visible');
        sideNavElement.classList.remove('expanded');
        activeButton.classList.remove('filter-active');
        activeButton.classList.remove('button--primary__active');
    });
};

const updateCategories = (categories: number[], isAsync: Boolean) => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    searchParams.delete('catids');
    searchParams.delete('page');
    categories.forEach((category) => searchParams.append('catids', category.toString()));
    url.search = searchParams.toString();

    if (isAsync) {
        history.pushState({}, '', url.toString());
        window.location.href = url.toString();
    } else {
        window.open(url.href, '_self');
    }
};

const initializeFilters = () => {
    const filterBoxsEl = document.querySelector('.filter-box');
    if (!filterBoxsEl) return;
    const data = (filterBoxsEl as HTMLElement).dataset;
    const { guuid, lang, abortText, submitText, filterChooseText, filterTitle, isAsync } = data;
    const isFilterAsync: Boolean = isAsync ? isAsync.toLowerCase() === 'true' : false;

    const mainUrl: string = window.location.origin;
    const apiUrl: string = `${mainUrl}/api/filter/categories/${guuid}/${lang}`;

    getCategories(apiUrl).then((filterCategories: FilterCategories) => {
        createFilter({
            mainElement: filterBoxsEl as HTMLElement,
            onSubmit: (categories) => updateCategories(categories, isFilterAsync),
            filterCategories,
            submitText: submitText || 'Submit',
            abortText: abortText || 'Abort',
            filterChooseText: filterChooseText || 'Choose filters', 
            filterTitle: filterTitle || 'Filter', 
            isAsync: isFilterAsync,
            language: lang || 'en',
        });
    });
};

filterSideNav();
setupOverlayClickClose();
initializeFilters();

export default createFilter;